import React, { useState } from 'react';

import { GoogleOAuthProvider } from '@react-oauth/google';
import { Link } from 'react-router-dom';

import GoogleLoginButton from '../../components/auth/GoogleLoginButton';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import AuthLayout from '../../components/layout/AuthLayout';
import { GOOGLE_CLIENT_ID } from '../../config/config';
import { useLogin } from '../../hooks/useLogin';

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [twoFactorCode, setTwoFactorCode] = useState<string>('');
  const [showTwoFactorInput, setShowTwoFactorInput] = useState<boolean>(false);
  const [tempAccessToken, setTempAccessToken] = useState<string | null>(null);

  const {
    handleLogin,
    handleTwoFactorVerification,
    handleGoogleSignIn,
    loading,
    errorMessage,
    setErrorMessage,
  } = useLogin({ setTempAccessToken, setShowTwoFactorInput });

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await handleLogin(email, password);
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await handleTwoFactorVerification(email, tempAccessToken, twoFactorCode);
  };

  return (
    <AuthLayout title="Login" description="Sign in to access your account.">
      <div className="mt-8">
        {errorMessage && (
          <div className="mb-4 text-sm text-red-600">{errorMessage}</div>
        )}

        {!showTwoFactorInput ? (
          <form onSubmit={handleSubmit}>
            <FormField
              id="email"
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@example.com"
            />
            <div className="mt-6">
              <FormField
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your Password"
              />
            </div>
            <div className="mt-6">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Loading...' : 'Sign In'}
              </Button>
            </div>
            <div className="mt-6">
              <span>or</span>
            </div>
            <div className="mt-6">
              {/*Google*/}
              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <GoogleLoginButton
                  handleGoogleSignIn={handleGoogleSignIn}
                  setErrorMessage={setErrorMessage}
                />
              </GoogleOAuthProvider>
            </div>
          </form>
        ) : (
          <form onSubmit={handleTwoFactorSubmit}>
            <div className="text-left">
              <p className="text-left text-gray-700 dark:text-cream mb-4">
                Enter the 2FA code from your authentication app.
              </p>
              <FormField
                id="twoFactorCode"
                label=""
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                maxLength={6}
                placeholder="6-digit code"
              />
            </div>
            <div className="mt-6">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </form>
        )}

        <p className="mt-6 text-sm text-center text-gray-400">
          Forgot your password?{' '}
          <Link
            to="/forgot-password"
            className="text-black focus:outline-none focus:underline hover:underline"
          >
            Reset it now
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default Login;
